
import { useState } from "react";
import { useLanguage } from "@/contexts/language";
import { PhotoCard } from "./PhotoCard";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { FullscreenDialog } from "./FullscreenDialog";
import { PaginationControls } from "./PaginationControls";
import { GalleryPhoto, GalleryCarouselProps } from "./types";
import { PhotoTitleEditDialog } from "../PhotoTitleEditDialog";

export function GalleryCarousel({ 
  photos, 
  onPhotoDeleted,
  sortOrder = 'newest'
}: GalleryCarouselProps) {
  const { language } = useLanguage();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isFullscreenDialogOpen, setIsFullscreenDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<GalleryPhoto | null>(null);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; // Show 9 photos per page (3x3 grid)
  
  // Sort photos based on sortOrder
  const sortedPhotos = [...photos].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });
  
  // Calculate pagination
  const totalPages = Math.ceil(sortedPhotos.length / itemsPerPage);
  const paginatedPhotos = sortedPhotos.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  const handleDeleteClick = (photo: GalleryPhoto) => {
    setSelectedPhoto(photo);
    setIsDeleteDialogOpen(true);
  };
  
  const handleFullscreenClick = (photoUrl: string) => {
    setSelectedPhotoUrl(photoUrl);
    setIsFullscreenDialogOpen(true);
  };
  
  const handleEditClick = (photo: GalleryPhoto) => {
    setSelectedPhoto(photo);
    setIsEditDialogOpen(true);
  };
  
  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {paginatedPhotos.map((photo) => (
          <PhotoCard
            key={photo.id}
            photo={photo}
            onDeleteClick={handleDeleteClick}
            onFullscreenClick={handleFullscreenClick}
            onEditClick={handleEditClick}
          />
        ))}
      </div>
      
      {/* Pagination Controls */}
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        photo={selectedPhoto}
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onPhotoDeleted={onPhotoDeleted}
      />
      
      {/* Fullscreen Image Dialog */}
      <FullscreenDialog
        imageUrl={selectedPhotoUrl}
        isOpen={isFullscreenDialogOpen}
        onOpenChange={setIsFullscreenDialogOpen}
      />
      
      {/* Edit Photo Dialog */}
      {selectedPhoto && (
        <PhotoTitleEditDialog
          photo={selectedPhoto}
          onTitleUpdated={onPhotoDeleted}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
        />
      )}
    </div>
  );
}
